import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Box,
  ShoppingBag,
  RefreshCcw,
  BarChart2,
  Store,
  ChefHat,
  Warehouse,
  ArrowRightLeft,
  TrendingUp,
  Users2,
  ListTodo,
  UserCircle2,
  ReceiptEuroIcon,
  LogOut,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  UserCheck,
  FileText,
  Calculator,
  ReceiptEuro,
  ReceiptCent,
} from 'lucide-react';
import { repeat } from 'lodash';

interface SidebarProps {
  isOpen: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  isLargeScreen: boolean;
  isSmallScreen: boolean;
}

export function Sidebar({
  isOpen,
  isExpanded,
  onToggleExpand,
  onClose,
  isLargeScreen,
  isSmallScreen,
}: SidebarProps) {
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const { unreadByPath } = useNotifications();
  const isRtl = language === 'ar';

  const displayName = user
    ? isRtl
      ? user.name
      : user.nameEn || user.name
    : t('header.guest') || 'Guest';

  const userRoleLabel = React.useMemo(() => {
    const roles = { 
      admin: 'مدير', 
      branch: 'فرع', 
      chef: 'شيف', 
      production: 'إنتاج',
      accountant: 'محاسب'
    };
    const rolesEn = { 
      admin: 'Admin', 
      branch: 'Branch', 
      chef: 'Chef', 
      production: 'Production',
      accountant: 'Accountant'
    };
    return user?.role ? (isRtl ? roles[user.role] : rolesEn[user.role]) : '';
  }, [user, isRtl]);

  const navItems = React.useMemo(() => {
    if (!user) return [];
    const base = [{ path: '/dashboard', icon: Home, label: t('dashboard') }];

    const roleItems: Record<string, { path: string; icon: any; label: string }[]> = {
      admin: [
        { path: '/products', icon: Box, label: t('products.manage') },
        { path: '/branches', icon: Store, label: t('branches.manage') },
        { path: '/chefs', icon: ChefHat, label: t('chefs.manage') },
        { path: '/departments', icon: Users2, label: t('departments') },
        { path: '/orders', icon: ShoppingBag, label: t('orders') },
        { path: '/dailyOrdersSummary', icon: ArrowRightLeft, label: t('dailyOrdersSummary') },
        { path: '/returns', icon: RefreshCcw, label: t('returns') },
        { path: '/returns-vs-orders', icon: ClipboardCheck, label: t('returns-vs-orders') },
                { path: '/Production-Report', icon: ChefHat, label: t('Production-Report') },
                        { path: '/departments-report', icon: ListTodo, label: t('departments-report') },

        { path: '/sales', icon: TrendingUp, label: t('sales') },
        { path: '/reports', icon: BarChart2, label: t('reports') },
        { path: '/profile', icon: UserCheck, label: t('settings') },
      ],
      branch: [
        { path: '/orders/new', icon: ShoppingBag, label: t('orders.create') },
        { path: '/branch-orders', icon: ShoppingBag, label: t('orders.review') },
        { path: '/branch-sales/new', icon: ListTodo, label: t('sales.create') },
        { path: '/branch-sales', icon: TrendingUp, label: t('sales.review') },
        { path: '/branch-returns', icon: RefreshCcw, label: t('returns.review') },
        { path: '/branch-inventory', icon: Warehouse, label: t('inventory') },
        { path: '/profile', icon: UserCheck, label: t('settings') },
      ],
      chef: [
        { path: '/production-tasks', icon: ListTodo, label: t('productionTasks') },
                { path: '/Production-Report', icon: ChefHat, label: t('Production-Report') },
                                        { path: '/departments-report', icon: ListTodo, label: t('departments-report') },

        { path: '/profile', icon: UserCheck, label: t('settings') },
      ],
      production: [
        { path: '/products', icon: Box, label: t('products.manage') },
        { path: '/departments', icon: Users2, label: t('departments') },
        { path: '/orders', icon: ShoppingBag, label: t('orders') },
        { path: '/dailyOrdersSummary', icon: ArrowRightLeft, label: t('dailyOrdersSummary') },
        { path: '/returns', icon: RefreshCcw, label: t('returns') },
        { path: '/returns-vs-orders', icon: ClipboardCheck, label: t('returns-vs-orders') },
        { path: '/Production-Report', icon: ChefHat, label: t('Production-Report') },
                        { path: '/departments-report', icon: ListTodo, label: t('departments-report') },
        { path: '/reports', icon: BarChart2, label: t('reports') },
        { path: '/profile', icon: UserCheck, label: t('settings') },
      ],
      accountant: [
        { path: '/orders', icon: ShoppingBag, label: t('orders') },
        { path: '/dailyOrdersSummary', icon: ArrowRightLeft, label: t('dailyOrdersSummary') },
        { path: '/returns', icon: RefreshCcw, label: t('returns') },
        { path: '/returns-vs-orders', icon: ClipboardCheck, label: t('returns-vs-orders') },
                { path: '/Production-Report', icon: ChefHat, label: t('Production-Report') },
                                { path: '/departments-report', icon: ListTodo, label: t('departments-report') },
        { path: '/sales', icon: TrendingUp, label: t('sales') },
        { path: '/reports', icon: BarChart2, label: t('reports') },
        { path: '/profile', icon: UserCheck, label: t('settings') },
      ],
    };

    return [...base, ...(roleItems[user.role] || [])];
  }, [user, t]);

  const renderBadge = (count: number, collapsed: boolean) => {
    if (!count) return null;
    return (
      <span
        className={`absolute -top-1 ${
          isRtl ? '-left-1' : '-right-1'
        } flex items-center justify-center w-4 h-4 text-[10px] font-bold
               bg-red-500 text-white rounded-full border border-white z-10`}
      >
        {count > 9 ? '9+' : count}
      </span>
    );
  };

  return (
    <AnimatePresence mode="wait">
      {(isOpen || isLargeScreen) && (
        <>
          {!isLargeScreen && isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={onClose}
            />
          )}

          <motion.aside
            initial={false}
            animate={{
              x: isLargeScreen ? 0 : isOpen ? 0 : isRtl ? '100%' : '-100%',
              width: isLargeScreen
                ? isExpanded
                  ? 224
                  : 72
                : isSmallScreen
                ? 'min(164px, 65vw)'
                : 'min(192px, 70vw)',
            }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            className="fixed top-16 bottom-0 bg-gradient-to-b from-amber-100/90 to-amber-100/60 backdrop-blur-xl shadow-xl border-r border-amber-200/70 z-40 flex flex-col"
          >
            <div className="h-3" />

            {!isLargeScreen && (
              <div className="px-3 pb-1 flex justify-end">
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-full bg-amber-200/80 hover:bg-amber-300 text-amber-800 transition-colors"
                  aria-label={t('close')}
                >
                  <XCircle size={16} />
                </button>
              </div>
            )}

            <nav className="flex-1 px-2.5 py-2 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-amber-400">
              {navItems.map((item) => {
                const count = unreadByPath[item.path] || 0;
                const collapsed = isLargeScreen && !isExpanded;

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end
                    onClick={() => !isLargeScreen && onClose()}
                    className={({ isActive }) =>
                      `group relative flex items-center rounded-lg px-2.5 py-2 transition-all duration-150 text-sm font-medium
                      ${collapsed ? ' px-0 py-2.5' : 'justify-start'}
                      ${
                        isActive
                          ? 'bg-amber-200/90 text-amber-900 shadow-sm'
                          : 'text-amber-800 hover:bg-amber-200/60'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <div className="flex items-center relative w-full min-w-0">
                        <item.icon
                          size={collapsed ? 20 : 18}
                          className={isActive ? 'text-amber-900' : 'text-amber-700 flex-shrink-0'}
                        />
                        {renderBadge(count, collapsed)}
                        {!collapsed && (
                          <span
                            className={`ml-3 truncate min-w-0 overflow-hidden whitespace-nowrap ${
                              isRtl ? 'mr-3 ml-0' : ''
                            }`}
                          >
                            {item.label}
                          </span>
                        )}
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </nav>

            <motion.div
              className="p-1 sm:p-2 border-t border-amber-200 bg-amber-50/80 flex flex-col gap-1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col gap-2 p-2 sm:p-3 rounded-lg bg-white/60 backdrop-blur-sm border border-amber-100/30">
                <div className={`flex ${isLargeScreen && !isExpanded ? 'justify-center' : 'items-center justify-between'}`}>
                  <div className={`flex items-center gap-2 min-w-0 ${isLargeScreen && !isExpanded ? 'justify-center w-full' : ''}`}>
                    <UserCircle2
                      size={isLargeScreen && !isExpanded ? 20 : isSmallScreen ? 16 : 18}
                      className="text-amber-600 flex-shrink-0"
                    />
                    <motion.span
                      className={`${
                        isLargeScreen && !isExpanded
                          ? 'hidden'
                          : 'block truncate font-semibold text-amber-900 text-xs sm:text-sm min-w-0 overflow-hidden whitespace-nowrap'
                      }`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={isLargeScreen && !isExpanded ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
                    >
                      {displayName}
                    </motion.span>
                  </div>

                  {userRoleLabel && isLargeScreen && isExpanded && (
                    <motion.span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        user?.role === 'admin'
                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                          : user?.role === 'branch'
                          ? 'bg-blue-100 text-blue-800 border border-blue-200'
                          : user?.role === 'chef'
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : user?.role === 'production'
                          ? 'bg-purple-100 text-purple-800 border border-purple-200'
                          : user?.role === 'accountant'
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                          : 'bg-gray-100 text-gray-800 border border-gray-200'
                      }`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      {userRoleLabel}
                    </motion.span>
                  )}
                </div>
              </div>

              <motion.button
                onClick={logout}
                className={`flex ${
                  isLargeScreen && !isExpanded ? 'justify-center' : 'items-center justify-center'
                } gap-2 p-2 sm:p-2.5 rounded-lg bg-gradient-to-r from-red-50/80 to-red-100/80 
                           text-red-700 hover:from-red-100/80 hover:to-red-200/80 hover:text-red-800 
                           transition-all duration-200 border border-red-200/30 shadow-sm 
                           hover:shadow-md hover:scale-[1.02] active:scale-[0.98]
                           font-medium text-xs sm:text-sm ${isLargeScreen && !isExpanded ? 'py-3' : ''}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label={t('header.logout')}
              >
                <LogOut
                  size={isLargeScreen && !isExpanded ? 20 : isSmallScreen ? 16 : 18}
                  className="text-red-500 flex-shrink-0"
                />
                <motion.span
                  className={`${
                    isLargeScreen && !isExpanded
                      ? 'hidden'
                      : 'block truncate min-w-0 overflow-hidden whitespace-nowrap'
                  }`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={isLargeScreen && !isExpanded ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
                >
                  {t('header.logout')}
                </motion.span>
              </motion.button>
            </motion.div>

            {isLargeScreen && (
              <motion.button
                onClick={onToggleExpand}
                className={`absolute top-20 -translate-y-1/2 w-6 h-6 rounded-full bg-amber-700 text-white
                  shadow-md flex items-center justify-center z-50 border border-white
                  ${isRtl ? 'left-0 -translate-x-1/2' : 'right-0 translate-x-1/2'}
                  hover:bg-amber-800 transition-colors`}
                aria-label={isExpanded ? t('sidebar.collapse') : t('sidebar.expand')}
              >
                {isExpanded ? (
                  isRtl ? <ChevronRight size={14} /> : <ChevronLeft size={14} />
                ) : isRtl ? (
                  <ChevronLeft size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
              </motion.button>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}